import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../../common/icons/Icon";
import styles from "./PlannerEventReservationDetails.module.scss";
import { ColorButton } from "../../../../common/components/button/colorButton/ColorButton";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { usePlannerEventReservationDetails } from "../hooks/usePlannerEventReservationDetails";
import { NotesBlock } from "../../../../common/components/common/NotesDialog/components/NotesBlock";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { PlannerEventReservationDetailsUser } from "./PlannerEventReservationDetailsUser";
import { PlannerEventReservationDetailsReservation } from "./PlannerEventReservationDetailsReservation";
import { PlannerEventReservationDetailsEmails } from "./PlannerEventReservationDetailsEmails";
import { PlannerEventReservationDetailsFlag } from "./PlannerEventReservationDetailsFlag";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { UserEventStatus } from "../../../../common/enums";
import { IPlannerEventModel } from "../models/plannerEventModel";

interface IPlannerEventReservationDetailsProps {
  userEventId: number;
  users: IPlannerEventUserModel[] | undefined;
  isEventPassed?: boolean;
  onHide: () => void;
  studioSettings: IStudioSettingsModel | undefined;
  event?: IPlannerEventModel;
}

export const PlannerEventReservationDetails = ({
  userEventId,
  users,
  isEventPassed = false,
  onHide,
  studioSettings,
  event,
}: IPlannerEventReservationDetailsProps) => {
  const { user, isDeleting, isMarkingPenaltyCancelled, disablePermanentDeletion, onTryPermamentDeletion, onTryPenaltyCancellation } =
    usePlannerEventReservationDetails(userEventId, onHide, users, event);

  if (!user) {
    return;
  }

  return (
    <div className={styles.root}>
      <div className={classNames(styles.userInfo, styles.common_container)}>
        <h3 className={styles.common_title}>
          <Icon type={IconType.Person} color="#6366f1" />
          Данни за потребителя
        </h3>
        <PlannerEventReservationDetailsUser user={user} />
      </div>

      <hr />

      <div className={classNames(styles.details, styles.common_container)}>
        <h3 className={styles.common_title}>
          <Icon type={IconType.File} color="#6366f1" />
          Данни за резервацията
        </h3>

        <PlannerEventReservationDetailsReservation
          user={user}
          isEventPassed={isEventPassed}
          studioSettings={studioSettings}
          event={event}
        />
      </div>

      <hr />

      <div className={classNames(styles.emails, styles.common_container)}>
        <h3 className={styles.common_title}>
          <Icon type={IconType.Envelope3} color="#6366f1" />
          Потвърдителни имейли
        </h3>
        <PlannerEventReservationDetailsEmails user={user} />
      </div>

      <hr />

      <div className={styles.common_container}>
        <h3 className={styles.common_title}>
          <Icon type={IconType.Flag} color="#6366f1" />
          Флаг
        </h3>
        <PlannerEventReservationDetailsFlag user={user} />
      </div>

      <hr />

      <div className={classNames(styles.notes, styles.common_container)}>
        <h3 className={styles.common_title}>
          <Icon type={IconType.Notes} color="#6366f1" />
          Бележки за резервацията
        </h3>

        <div className={styles.emails_content}>
          <NotesBlock
            notes={user.notes}
            entityId={user.userEventId}
            noResultsLabel="Към момента няма добавени бележки за тази резервация."
            createEndpoint={endpointsMetadata.planner.addUserEventNote}
            updateEndpoint={endpointsMetadata.planner.updateUserEventNote}
            deleteEndpoint={endpointsMetadata.planner.deleteUserEventNote}
            invalidateKeys={[[endpointsMetadata.planner.getEvent]]}
          />
        </div>
      </div>

      <RenderWhen
        condition={
          !user.isCancelled &&
          (user.status === UserEventStatus.Confirmed ||
            user.status === UserEventStatus.Pending)
        }
      >
        <hr className={styles.permanent_deletion_hr} />
        <div
          className={classNames(
            styles.permanent_deletion,
            styles.common_container
          )}
        >
          <ColorButton
            className={styles.permanent_deletion_button}
            label={"Маркирай резервацията като отказана с очаквано плащане"}
            backgroundColorHex={"#f97316"}
            textColorHex={"#fff"}
            loading={!!isMarkingPenaltyCancelled}
            onClick={onTryPenaltyCancellation}
          />
        </div>
      </RenderWhen>

      <hr className={styles.permanent_deletion_hr} />
      <div
        className={classNames(
          styles.permanent_deletion,
          styles.common_container
        )}
      >
        <ColorButton
          className={styles.permanent_deletion_button}
          label={"Перманентно изтриване на резервацията"}
          backgroundColorHex={"#ef4444"}
          textColorHex={"#fff"}
          loading={!!isDeleting}
          tooltip={
            "Потвърдена и платена резервация не може да бъде изтрита перманентно. Първо трябва да маркирате потребителя като непристигнал/неплатил."
          }
          disabled={disablePermanentDeletion}
          onClick={onTryPermamentDeletion}
        />
      </div>
    </div>
  );
};
